// Chart Tabs Component
// Chrome-like tabbed interface for multiple charts
// Architecture: v4.0.0 Multiple Charts Implementation

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Flex, Text, IconButton, Button, Tooltip, HStack, Spinner } from '@chakra-ui/react';
import { CloseIcon, AddIcon, DragHandleIcon, TimeIcon } from '@chakra-ui/icons';
import { useMultipleCharts } from './MultipleChartsContext';
import { ChartTabConfig } from '../../../types/MultipleCharts';
import ChartManagement from './ChartManagement';
import { useTabDragAndDrop, DraggableTab } from './TabDragAndDrop';
import TabContextMenu, { ContextMenuPosition } from './TabContextMenu';
import { SettingsToggle, SettingsPanel } from './SettingsPanel';
import { globalEventStream } from './universal_architecture/core/EventStream';
import { formatChartDataForClipboard, copyToClipboard } from './utils/formatChartDataForClipboard';
import { ChartViewButtons } from './ChartViewButtons';
import type { ChartViewMode } from '../../../types/ChartViewTypes';
import { useToast } from '@chakra-ui/react';
import './ChartTabs.css';

interface ChartTabsProps {
  onAddChart?: () => void;
  maxVisibleTabs?: number;
  className?: string;
  shareMode?: boolean;  // If true, disable tab creation and closing
}

/**
 * Individual Chart Tab Component
 * Single tab with title, loading state, and close button
 */
interface ChartTabProps {
  config: ChartTabConfig;
  isActive: boolean;
  onActivate: (chartId: string) => void;
  onClose: (chartId: string) => void;
  onContextMenu?: (chartId: string, event: React.MouseEvent) => void;
  onAddCurrentTimeSnapshot?: (chartId: string) => Promise<void>;
  isDraggable?: boolean;
  shareMode?: boolean;  // If true, hide close button
  // Drag and drop props
  index?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
  dragHandlers?: any;
}

const ChartTab: React.FC<ChartTabProps> = ({
  config,
  isActive,
  onActivate,
  onClose,
  onContextMenu,
  onAddCurrentTimeSnapshot,
  isDraggable = false,
  shareMode = false,
  index = 0,
  isDragging = false,
  isDragOver = false,
  dragHandlers
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    if (!isActive) {
      onActivate(config.chartId);
    }
  }, [isActive, onActivate, config.chartId]);

  const handleClose = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (config.canClose) {
      onClose(config.chartId);
    }
  }, [onClose, config.chartId, config.canClose]);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    if (onContextMenu) {
      onContextMenu(config.chartId, event);
    }
  }, [onContextMenu, config.chartId]);

  // CSS classes for styling
  const tabClasses = `chart-tab ${isActive ? 'active' : 'inactive'}`;

  const tabContent = (
    <Flex
      className={tabClasses}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-chart-id={config.chartId}
      tabIndex={0}
    >
      {/* Drag Handle (if draggable) */}
      {isDraggable && (
        <Box className="chart-tab-drag-handle">
          <DragHandleIcon w={3} h={3} color="gray.400" />
        </Box>
      )}

      {/* Tab Title */}
      <Text
        className={`chart-tab-title ${isDraggable ? 'with-drag-handle' : ''}`}
        isTruncated
      >
        {config.title}
      </Text>

      {/* Loading Indicator */}
      {config.isLoading && (
        <Box className="chart-tab-loading" />
      )}

      {/* Unsaved Changes Indicator */}
      {config.hasUnsavedChanges && (
        <Box
          className="chart-tab-unsaved"
          title="Unsaved changes"
        />
      )}

      {/* Close Button */}
      {!shareMode && config.canClose && (isHovered || isActive) && (
        <IconButton
          aria-label={`Close ${config.title}`}
          icon={<CloseIcon />}
          size="xs"
          variant="ghost"
          onClick={handleClose}
          className="chart-tab-button"
          color="gray.500"
          _hover={{
            color: 'red.500',
            bg: 'red.50'
          }}
        />
      )}
    </Flex>
  );

  // Wrap with DraggableTab if drag handlers are provided
  if (isDraggable && dragHandlers) {
    return (
      <DraggableTab
        index={index}
        isDragging={isDragging}
        isDragOver={isDragOver}
        dragHandlers={dragHandlers}
        disabled={!isDraggable}
      >
        {tabContent}
      </DraggableTab>
    );
  }

  return tabContent;
};

/**
 * Chart Tabs Container Component
 * Main tabbed interface for multiple charts
 */
export const ChartTabs: React.FC<ChartTabsProps> = ({
  onAddChart,
  maxVisibleTabs = 5,
  className,
  shareMode = false
}) => {
  const {
    charts,
    activeChartId,
    loadedChartIds,
    switchToChart,
    closeTab,
    reorderTabs,
    workspace,
    addCurrentTimeSnapshot,
    updateChart
  } = useMultipleCharts();

  const [contextMenuChart, setContextMenuChart] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null);
  const [showChartManagement, setShowChartManagement] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeView, setActiveView] = useState<ChartViewMode>('chakra');
  const toast = useToast();

  // Drag and drop functionality
  const { dragHandlers, isDraggedTab, isDragOverTab } = useTabDragAndDrop(reorderTabs);

  // Track renamed chart names to trigger tab title refresh
  // This is needed because updateChart now mutates Map in-place for metadata-only changes
  const [nameVersion, setNameVersion] = useState(0);

  useEffect(() => {
    const subscription = globalEventStream.subscribe('chart.field.renamed', (event) => {
      const { field, chartId } = event.metadata || {};
      // Only trigger refresh for chart_name field and loaded charts
      if (field === 'chart_name' && chartId && loadedChartIds.includes(chartId)) {
        setNameVersion(v => v + 1);
      }
    });
    return () => subscription.unsubscribe();
  }, [loadedChartIds]);

  // Generate tab configurations
  const tabConfigs = useMemo((): ChartTabConfig[] => {
    return loadedChartIds.map(chartId => {
      const chart = charts.get(chartId);

      if (!chart) {
        // Use tabMeta for unloaded charts (lazy loading support)
        const tabMetaEntry = workspace.tabMeta?.[chartId];
        return {
          chartId,
          title: tabMetaEntry?.name || `Chart ${chartId.substring(0, 8)}...`,
          isActive: chartId === activeChartId,
          isLoading: false,
          canClose: true,
          hasUnsavedChanges: false,
          lastAccessed: tabMetaEntry?.lastAccessed || new Date()
        };
      }

      // Generate title from API response (current name) with fallback to metadata (renamed name) 
      const title = chart.apiResponse?.basic_info?.chart_name || 
                   chart.metadata?.name || 
                   (chart.metadata?.chartDateTime ? 
                    `${chart.metadata.chartDateTime.location || 'Chart'} - ${chart.metadata.chartDateTime.date} ${chart.metadata.chartDateTime.time}` :
                    `Chart ${chartId.substring(6, 12)}`
                   );
      
      
      return {
        chartId,
        title,
        isActive: chartId === activeChartId,
        isLoading: chart.isLoading,
        canClose: true, // Allow closing any tab including last one
        hasUnsavedChanges: false, // TODO: implement unsaved changes detection
        lastAccessed: chart.lastAccessed
      };
    });
  }, [charts, loadedChartIds, activeChartId, workspace.tabMeta, nameVersion]);

  // Handle tab activation
  const handleTabActivate = useCallback((chartId: string) => {
    switchToChart(chartId);
  }, [switchToChart]);

  // Handle tab close
  const handleTabClose = useCallback((chartId: string) => {
    closeTab(chartId);
  }, [closeTab]);

  // Handle context menu
  const handleContextMenu = useCallback((chartId: string, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuChart(chartId);
    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY
    });
  }, []);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenuChart(null);
    setContextMenuPosition(null);
  }, []);

  // Handle add new chart
  const handleAddChart = useCallback(() => {
    if (onAddChart) {
      onAddChart();
    } else {
      // Default behavior: open import modal or recent charts
    }
  }, [onAddChart]);

  // Handle copy chart data to clipboard
  const handleCopyClick = useCallback(async () => {
    if (!activeChartId) return;

    const chart = charts.get(activeChartId);
    if (!chart?.apiResponse) {
      toast({
        title: 'No data',
        description: 'Chart data not available',
        status: 'warning',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    const formattedData = formatChartDataForClipboard(chart.apiResponse);
    const success = await copyToClipboard(formattedData);

    if (success) {
      toast({
        title: 'Copied!',
        description: 'Chart data copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    } else {
      toast({
        title: 'Failed',
        description: 'Could not copy to clipboard',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    }
  }, [activeChartId, charts, toast]);

  // Switch chart view mode (chakra / horaClock / rashiChakra)
  const switchChartView = useCallback((targetView: ChartViewMode) => {
    if (!activeChartId) return;
    // Toggle: clicking active view returns to chakra
    const newView = activeView === targetView ? 'chakra' : targetView;
    setActiveView(newView);

    // Persist single key
    updateChart(activeChartId, { settings: { activeView: newView } } as any);

    // Single event — renderer handles all layer visibility
    globalEventStream.emit({
      id: `view-switch-${activeChartId}-${Date.now()}`,
      type: 'chart.view.switch',
      source: 'ChartTabs',
      timestamp: Date.now(),
      data: { chartId: activeChartId, view: newView }
    });
  }, [activeChartId, activeView, updateChart]);

  // Sync view state when active chart changes
  useEffect(() => {
    if (!activeChartId) {
      setActiveView('chakra');
      return;
    }
    const chart = charts.get(activeChartId);
    const settings = (chart as any)?.settings;
    setActiveView(settings?.activeView || 'chakra');
  }, [activeChartId, charts]);

  // Check if we have overflow
  const hasOverflow = tabConfigs.length > maxVisibleTabs;
  const visibleTabs = hasOverflow ? tabConfigs.slice(0, maxVisibleTabs - 1) : tabConfigs;
  const overflowCount = hasOverflow ? tabConfigs.length - maxVisibleTabs + 1 : 0;

  if (tabConfigs.length === 0) {
    return (
      <Box className={className}>
        <Flex
          align="stretch"
          bg="transparent"
          px={0}
          py={0}
          overflowX="hidden"
        >
          {activeChartId && (
            <ChartViewButtons
              activeChartId={activeChartId}
              activeView={activeView}
              onSwitchView={switchChartView}
              onCopy={handleCopyClick}
            />
          )}
          {/* Settings Button - ALWAYS visible */}
          <SettingsToggle
            onClick={() => setShowSettings(!showSettings)}
            isActive={showSettings}
          />

          {/* Recent Charts Button - ALWAYS visible */}
          <Tooltip label="Recent charts" hasArrow>
            <IconButton
              aria-label="Recent charts"
              icon={<TimeIcon />}
              size="sm"
              variant="ghost"
              onClick={() => setShowChartManagement(true)}
              height="32px"
              minWidth="32px"
              _hover={{ bg: 'gray.50' }}
              color="gray.600"
              ml={1}
            />
          </Tooltip>

          {/* Load First Chart Button */}
          <Button
            leftIcon={<AddIcon />}
            variant="outline"
            size="sm"
            onClick={handleAddChart}
            colorScheme="blue"
            ml={2}
            height="32px"
          >
            Load First Chart
          </Button>

          {/* Spacer */}
          <Box flex={1} />
          
          {/* Workspace Info */}
          <Box
            display="flex"
            alignItems="center"
            px={3}
            fontSize="12px"
            color="var(--text-secondary, #666)"
            fontFamily="var(--font-ui-primary)"
          >
            0/{workspace.maxOpenCharts} charts
          </Box>
        </Flex>

        {/* Chart Management Modal */}
        <ChartManagement
          isOpen={showChartManagement}
          onClose={() => setShowChartManagement(false)}
        />

        {/* Settings Panel */}
        <SettingsPanel 
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Flex
        align="stretch"
        bg="transparent"
        px={0}
        py={0}
        overflowX="auto"
        className="chart-tabs-row"
      >
        {activeChartId && (
          <ChartViewButtons
            activeChartId={activeChartId}
            activeView={activeView}
            onSwitchView={switchChartView}
            onCopy={handleCopyClick}
          />
        )}
        {/* Settings Button */}
        <SettingsToggle
          onClick={() => setShowSettings(!showSettings)}
          isActive={showSettings}
        />

        {/* Visible Tabs */}
        {visibleTabs.map((config, index) => (
          <ChartTab
            key={config.chartId}
            config={config}
            isActive={config.isActive}
            onActivate={handleTabActivate}
            onClose={handleTabClose}
            onContextMenu={handleContextMenu}
            onAddCurrentTimeSnapshot={addCurrentTimeSnapshot}
            isDraggable={tabConfigs.length > 1}
            shareMode={shareMode}
            index={index}
            isDragging={isDraggedTab(index)}
            isDragOver={isDragOverTab(index)}
            dragHandlers={dragHandlers}
          />
        ))}

        {/* Overflow Indicator */}
        {hasOverflow && (
          <Tooltip label={`${overflowCount} more charts`} hasArrow>
            <Box
              minWidth="40px"
              height="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="gray.100"
              borderRadius="8px 8px 0 0"
              borderWidth="1px"
              borderBottomWidth="0"
              borderColor="gray.300"
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
            >
              <Text fontSize="sm" color="gray.600">
                +{overflowCount}
              </Text>
            </Box>
          </Tooltip>
        )}

        {/* Chart Actions */}
        <HStack spacing={1} ml={2}>
          {/* Recent Charts Button */}
          <Tooltip label="Recent charts" hasArrow>
            <IconButton
              aria-label="Recent charts"
              icon={<TimeIcon />}
              size="sm"
              variant="ghost"
              onClick={() => setShowChartManagement(true)}
              height="32px"
              minWidth="32px"
              _hover={{ bg: 'gray.50' }}
              color="gray.600"
            />
          </Tooltip>

          {/* Add Chart Button - hidden in share mode */}
          {!shareMode && loadedChartIds.length < workspace.maxOpenCharts && (
            <Tooltip label="Add new chart" hasArrow>
              <IconButton
                aria-label="Add chart"
                icon={<AddIcon />}
                size="sm"
                variant="ghost"
                onClick={handleAddChart}
                height="32px"
                minWidth="32px"
                _hover={{ bg: 'blue.50' }}
                color="blue.500"
              />
            </Tooltip>
          )}
        </HStack>

        {/* Workspace Info */}
        <Box flex={1} />
        <Box
          display="flex"
          alignItems="center"
          px={3}
          fontSize="12px"
          color="var(--text-secondary, #666)"
          fontFamily="var(--font-ui-primary)"
        >
          {loadedChartIds.length}/{workspace.maxOpenCharts} charts
        </Box>
      </Flex>

      {/* Chart Management Modal */}
      <ChartManagement
        isOpen={showChartManagement}
        onClose={() => setShowChartManagement(false)}
      />

      {/* Tab Context Menu */}
      <TabContextMenu
        chartId={contextMenuChart}
        position={contextMenuPosition}
        onClose={handleContextMenuClose}
      />

      {/* Settings Panel */}
      <SettingsPanel 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </Box>
  );
};

export default ChartTabs;